/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import {
  GodavariApplicationPayload,
  GodavariBatchPayloads,
  GodavariConnectorMethods,
  GodavariConnectorPayloads,
  GodavariDeviceInformation,
  GodavariEmptyBatch,
  GodavariFrontendConfiguration,
} from '../model/GodavariPayload';
import { GodavariAnalyticsReporterConfiguration } from '../../configuration/GodavariAnalyticsReporterConfiguration';

/** Normalize SDK platform names to the values expected by the Godavari vendor. */
export function normalizeGodavariPlatform(platform: string | undefined): string | undefined {
  if (!platform) return platform;
  const normalized = platform.toLowerCase().replace(/[ _-]/g, '');
  if (normalized === 'web' || normalized === 'webdesktop') return 'Web';
  if (normalized === 'mweb' || normalized === 'webmobile' || normalized === 'mobileweb') return 'MWEB';
  return platform;
}

const DEFAULT_BEACON_BASE_URL = 'https://api-godavari.sonyliv.com/beacon';
const DEFAULT_HEARTBEAT_INTERVAL_MS = 40_000;
const DEFAULT_AD_HEARTBEAT_INTERVAL_MS = 10_000;
const DEFAULT_ERROR_RETRY_INTERVAL_MS = 30_000;

export interface GodavariFrontendConnector {
  setDefaultConfig(configuration: GodavariFrontendConfiguration): void;
  updateForeignCountryStatus(isForeignCountry: boolean): void;
  appStart(eventName: 'AppSessionStart', payload: GodavariApplicationPayload): void;
  /** Populated by the vendor on appStart; `as-si` is `<platform>-<as-did>-<epoch>`. */
  masterSPNData?: Record<string, unknown>;
}

export interface GodavariConnector extends GodavariConnectorMethods {
  registerHeartbeat(callback: () => GodavariBatchPayloads['videoSessionHeartbeat'] | GodavariEmptyBatch): void;
  registerPulse(callback: () => GodavariBatchPayloads['videoSessionPulse'] | GodavariEmptyBatch): void;
  lastHeartbeatRequest(): void;
  lastPulseRequest(): void;
  updateDeviceInformation?(information: GodavariDeviceInformation): void;
  /** Vendor startup error stops its heartbeat timer; explicit recovery resumes it. */
  startHeartbeat?(): void;
  /** Clears per-attempt lifecycle guards when recovering a failed startup. */
  updatePlayerFlagStatus?(): void;
}

declare global {
  interface Window {
    ANALYTICS_CONNECTOR_FRONTEND?: GodavariFrontendConnector;
    ANALYTICS_CONNECTOR?: new () => GodavariConnector;
  }
}

export const emptyGodavariBatch = (): GodavariEmptyBatch => ({ event_data_list: [] });

/** Check script availability before replacing an existing playback session. */
export function validateGodavariSdk(): GodavariFrontendConnector {
  const frontend = window.ANALYTICS_CONNECTOR_FRONTEND;
  if (!frontend) {
    throw new TypeError('Godavari frontend connector is missing; load the Godavari SDK script first.');
  }
  if (typeof window.ANALYTICS_CONNECTOR !== 'function') {
    throw new TypeError('Godavari playback connector is missing; load the Godavari SDK script first.');
  }
  return frontend;
}

export function configureGodavariFrontend(
  frontend: GodavariFrontendConnector,
  configuration: GodavariAnalyticsReporterConfiguration,
): void {
  frontend.setDefaultConfig({
    BEACON_BASE_URL: configuration.beaconBaseUrl || DEFAULT_BEACON_BASE_URL,
    GODAVARI_AD_HEARTBEAT_INTERVAL: String(configuration.adHeartbeatInterval ?? DEFAULT_AD_HEARTBEAT_INTERVAL_MS),
    GODAVARI_ENABLED: configuration.enabled ?? true,
    GODAVARI_ERROR_RETRYINTERVAL: configuration.errorRetryInterval ?? DEFAULT_ERROR_RETRY_INTERVAL_MS,
    GODAVARI_HEARTBEAT_INTERVAL: String(configuration.heartbeatInterval ?? DEFAULT_HEARTBEAT_INTERVAL_MS),
  });
  const countryCode = configuration.countryCode || configuration.appSessionData?.app_geo_location || '';
  frontend.updateForeignCountryStatus(String(countryCode).toLowerCase() !== 'in');
}

export function startGodavariApplication(payload: GodavariApplicationPayload): void {
  const frontend = window.ANALYTICS_CONNECTOR_FRONTEND;
  if (!frontend) {
    throw new TypeError('Godavari frontend connector is no longer available.');
  }
  frontend.updateForeignCountryStatus(String(payload.app_geo_location).toLowerCase() !== 'in');
  frontend.appStart('AppSessionStart', payload);
}

/** Returns the vendor-built app session id (`as-si`), available once the application session has started. */
export function getGodavariApplicationSessionId(): string | undefined {
  const value = window.ANALYTICS_CONNECTOR_FRONTEND?.masterSPNData?.['as-si'];
  return typeof value === 'string' && value ? value : undefined;
}

/**
 * Stores beacons in the vendor's localStorage queue without posting them while the page unloads,
 * the way the vendor treats AppSessionEnd. The vendor replays queued beacons of a previous
 * session, with their original ids, on the next launch. Returns a function that restores
 * delivery in case the page was not actually unloaded.
 */
export function holdGodavariDelivery(): () => void {
  const vendor = window as Window & { networkOnProcess?: boolean };
  const previous = vendor.networkOnProcess;
  vendor.networkOnProcess = false;
  return () => {
    setTimeout(() => {
      vendor.networkOnProcess = previous;
    }, 0);
  };
}

export function createGodavariConnector(deviceInformation: GodavariDeviceInformation): GodavariConnector {
  const Constructor = window.ANALYTICS_CONNECTOR;
  if (!Constructor) {
    throw new TypeError('Godavari playback connector is no longer available.');
  }
  const connector = new Constructor();
  for (const method of ['registerHeartbeat', 'registerPulse', 'lastHeartbeatRequest', 'lastPulseRequest'] as const) {
    if (typeof connector[method] !== 'function') {
      throw new TypeError(`Godavari connector does not implement ${method}().`);
    }
  }
  connector.updateDeviceInformation?.(deviceInformation);
  return connector;
}

export interface GodavariBatchCallbacks {
  heartbeat: () => GodavariBatchPayloads['videoSessionHeartbeat'] | GodavariEmptyBatch;
  pulse: () => GodavariBatchPayloads['videoSessionPulse'] | GodavariEmptyBatch;
}

export function registerGodavariCallbacks(
  connector: GodavariConnector,
  callbacks: GodavariBatchCallbacks,
  isCurrentSession: () => boolean,
): void {
  // Vendor timers can retain callbacks even after the session is replaced.
  connector.registerHeartbeat(() => (isCurrentSession() ? callbacks.heartbeat() : emptyGodavariBatch()));
  connector.registerPulse(() => (isCurrentSession() ? callbacks.pulse() : emptyGodavariBatch()));
}

export function closeGodavariSession(connector: GodavariConnector, sendSessionEnd: () => void): void {
  // videoSessionEnd only stops the SDK pulse timer. Drain both batches first.
  connector.lastPulseRequest();
  connector.lastHeartbeatRequest();
  sendSessionEnd();
  connector.registerHeartbeat(emptyGodavariBatch);
  connector.registerPulse(emptyGodavariBatch);
}

export function sendGodavariPayload<K extends keyof GodavariConnectorPayloads>(
  connector: GodavariConnector,
  method: K,
  createPayload: () => GodavariConnectorPayloads[K],
): void {
  const methods: GodavariConnectorMethods = connector;
  if (typeof methods[method] !== 'function') {
    throw new TypeError(`Godavari connector does not implement ${method}().`);
  }
  const payload = createPayload();
  methods[method](payload.video_event_type, payload);
}
